import { Add, DeleteOutline, RotateLeft } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { invoiceEditorInnerBorder, invoiceEditorSectionCardSx } from "../../../ui/invoiceEditorSectionSurface";
import { sanitizeAmountDraft } from "../shared/utils";

export interface InvoiceExtraLineItemAllocation {
  eventUid: string;
  amount: string;
  unitPrice?: string;
}

export type InvoiceExtraLineItem = {
  id: string;
  title: string;
  amount: string;
  unitPrice?: string;
  description?: string;
  isPassedItem?: boolean;
  originalAmount?: string;
  allocations?: InvoiceExtraLineItemAllocation[];
};

export interface AvailableInvoiceVisit {
  uid: string;
  label: string;
}

type Props = {
  items: InvoiceExtraLineItem[];
  availableVisits?: AvailableInvoiceVisit[];
  onAdd: () => void;
  onRemove: (id: string) => void;
  onChange: (id: string, field: "title" | "amount" | "description", value: string) => void;
  onChangeItem?: (id: string, updated: InvoiceExtraLineItem) => void;
  totalAmount: number;
  showValidation?: boolean;
  onResetAmount?: (id: string) => void;
  /** `embedded` sits inside Line Items, after the last service row. */
  variant?: "card" | "embedded";
};

const roundMoney = (val: number): number => Math.round(val * 100) / 100;

function calculateAllocationsTotal(allocations?: InvoiceExtraLineItemAllocation[]): string {
  if (!allocations || allocations.length === 0) return "0";
  const sum = allocations.reduce((acc, a) => {
    const n = Number.parseFloat(String(a.amount || "0"));
    return acc + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
  return String(roundMoney(sum));
}

function ExtraLineItemRows({
  items,
  availableVisits = [],
  onAdd,
  onRemove,
  onChange,
  onChangeItem,
  totalAmount,
  showValidation = false,
  onResetAmount,
  showFooterTotal,
}: Props & { showFooterTotal: boolean }) {
  const hasMultipleVisits = availableVisits.length > 1;

  const notifyItemChange = (item: InvoiceExtraLineItem, updated: InvoiceExtraLineItem) => {
    if (onChangeItem) {
      onChangeItem(item.id, updated);
    } else {
      if (updated.title !== item.title) onChange(item.id, "title", updated.title);
      if (updated.description !== item.description) onChange(item.id, "description", updated.description || "");
      if (updated.amount !== item.amount) onChange(item.id, "amount", updated.amount);
    }
  };

  return (
    <>
      <Stack spacing={0} divider={<Divider flexItem />}>
        {items.map((item) => {
          const parsedAmount = Number.parseFloat(String(item.amount || "0"));
          const hasAmount = Number.isFinite(parsedAmount) && parsedAmount > 0;
          const hasTitle = item.title.trim().length > 0;
          const titleError = showValidation && !hasTitle;

          const isModified = item.isPassedItem && item.amount !== item.originalAmount;

          const currentAllocations = item.allocations ?? [];
          const selectedVisitsCount = currentAllocations.length;
          const noVisitsSelectedError = showValidation && hasMultipleVisits && selectedVisitsCount === 0;
          const amountError = showValidation && (!hasAmount || noVisitsSelectedError);

          // Handlers for multi-visit allocation
          const handleUnitPriceChange = (rawUnitPrice: string) => {
            const cleanUnitPrice = sanitizeAmountDraft(rawUnitPrice);
            let updatedAllocations = currentAllocations;

            // If user enters a unit price and some visits are selected, update their amounts to the new unit price
            if (currentAllocations.length > 0) {
              updatedAllocations = currentAllocations.map((a) => ({
                ...a,
                amount: cleanUnitPrice,
                unitPrice: cleanUnitPrice,
              }));
            }

            const newTotal = calculateAllocationsTotal(updatedAllocations);
            notifyItemChange(item, {
              ...item,
              unitPrice: cleanUnitPrice,
              allocations: updatedAllocations,
              amount: newTotal,
            });
          };

          const handleToggleVisit = (visitUid: string) => {
            const isSelected = currentAllocations.some((a) => a.eventUid === visitUid);
            let updatedAllocations: InvoiceExtraLineItemAllocation[];

            if (isSelected) {
              updatedAllocations = currentAllocations.filter((a) => a.eventUid !== visitUid);
            } else {
              const defaultAmt = item.unitPrice || item.amount || "0";
              updatedAllocations = [
                ...currentAllocations,
                { eventUid: visitUid, amount: defaultAmt, unitPrice: defaultAmt },
              ];
            }

            const newTotal = calculateAllocationsTotal(updatedAllocations);
            notifyItemChange(item, {
              ...item,
              allocations: updatedAllocations,
              amount: newTotal,
            });
          };

          const handleVisitAmountChange = (visitUid: string, rawAmt: string) => {
            const cleanAmt = sanitizeAmountDraft(rawAmt);
            const updatedAllocations = currentAllocations.map((a) =>
              a.eventUid === visitUid ? { ...a, amount: cleanAmt } : a,
            );

            const newTotal = calculateAllocationsTotal(updatedAllocations);
            notifyItemChange(item, {
              ...item,
              allocations: updatedAllocations,
              amount: newTotal,
            });
          };

          const handleSelectAllVisits = () => {
            const defaultAmt = item.unitPrice || "0";
            const updatedAllocations: InvoiceExtraLineItemAllocation[] = availableVisits.map((v) => {
              const existing = currentAllocations.find((a) => a.eventUid === v.uid);
              return existing ?? { eventUid: v.uid, amount: defaultAmt, unitPrice: defaultAmt };
            });

            const newTotal = calculateAllocationsTotal(updatedAllocations);
            notifyItemChange(item, {
              ...item,
              allocations: updatedAllocations,
              amount: newTotal,
            });
          };

          const handleClearVisits = () => {
            notifyItemChange(item, {
              ...item,
              allocations: [],
              amount: "0",
            });
          };

          return (
            <Box key={item.id} sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 180px auto" },
                  gap: 1.5,
                  alignItems: "start",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  <TextField
                    size="small"
                    label="Title"
                    value={item.title}
                    onChange={(e) => {
                      const updated = { ...item, title: e.target.value };
                      notifyItemChange(item, updated);
                    }}
                    placeholder="e.g. Window Cleaning"
                    fullWidth
                    error={titleError}
                    helperText={titleError ? "Title is required." : undefined}
                  />
                  <TextField
                    size="small"
                    label="Description (optional)"
                    value={item.description || ""}
                    onChange={(e) => {
                      const updated = { ...item, description: e.target.value };
                      notifyItemChange(item, updated);
                    }}
                    placeholder="e.g. Exterior and interior window cleaning"
                    fullWidth
                    multiline
                    minRows={1}
                  />
                </Box>

                {hasMultipleVisits ? (
                  <TextField
                    size="small"
                    label="Unit Price"
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    value={item.unitPrice ?? ""}
                    onChange={(e) => handleUnitPriceChange(e.target.value)}
                    placeholder="0.00"
                    onWheel={(e) => {
                      (e.target as HTMLInputElement).blur();
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    helperText="Base price per visit"
                  />
                ) : (
                  <TextField
                    size="small"
                    label="Amount"
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    value={item.amount}
                    onChange={(e) => {
                      const clean = sanitizeAmountDraft(e.target.value);
                      const allocations =
                        availableVisits.length === 1
                          ? [{ eventUid: availableVisits[0].uid, amount: clean, unitPrice: clean }]
                          : item.allocations;
                      notifyItemChange(item, {
                        ...item,
                        amount: clean,
                        unitPrice: clean,
                        allocations,
                      });
                    }}
                    onWheel={(e) => {
                      (e.target as HTMLInputElement).blur();
                    }}
                    error={amountError}
                    helperText={amountError ? "Amount must be greater than 0." : undefined}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      endAdornment: isModified ? (
                        <InputAdornment position="end">
                          <Button
                            size="small"
                            onClick={() => onResetAmount?.(item.id)}
                            sx={{
                              color: "var(--fx-surface-texttertiary, #94A3B8)",
                              textTransform: "none",
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              minWidth: 0,
                              p: 0.5,
                              gap: 0.5,
                              display: "flex",
                              alignItems: "center",
                              "&:hover": {
                                backgroundColor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
                              },
                            }}
                          >
                            RESET
                            <RotateLeft sx={{ fontSize: 16 }} />
                          </Button>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                )}

                <IconButton
                  aria-label="remove extra line item"
                  onClick={() => onRemove(item.id)}
                  size="small"
                  sx={{ justifySelf: { xs: "end", sm: "center" } }}
                >
                  <DeleteOutline />
                </IconButton>
              </Box>

              {/* Multi-visit allocation panel */}
              {hasMultipleVisits ? (
                <Box
                  sx={{
                    bgcolor: "rgba(0, 0, 0, 0.02)",
                    borderRadius: 1.5,
                    border: noVisitsSelectedError ? "1px solid #d32f2f" : "1px solid rgba(0, 0, 0, 0.08)",
                    p: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: noVisitsSelectedError ? "error.main" : "text.secondary" }}>
                      Applies to visits ({selectedVisitsCount} of {availableVisits.length} selected):
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        onClick={handleSelectAllVisits}
                        sx={{ fontSize: "0.75rem", textTransform: "none", py: 0.25, px: 0.75 }}
                      >
                        Select all
                      </Button>
                      <Button
                        size="small"
                        onClick={handleClearVisits}
                        color="inherit"
                        sx={{ fontSize: "0.75rem", textTransform: "none", py: 0.25, px: 0.75 }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Box>

                  {noVisitsSelectedError ? (
                    <Typography variant="caption" color="error">
                      Please select at least one visit for this extra service.
                    </Typography>
                  ) : null}

                  <Stack spacing={1}>
                    {availableVisits.map((visit) => {
                      const allocation = currentAllocations.find((a) => a.eventUid === visit.uid);
                      const isChecked = Boolean(allocation);

                      return (
                        <Box
                          key={visit.uid}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1,
                            p: 0.75,
                            borderRadius: 1,
                            bgcolor: isChecked ? "rgba(25, 118, 210, 0.04)" : "transparent",
                            border: isChecked ? "1px solid rgba(25, 118, 210, 0.15)" : "1px solid transparent",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={isChecked}
                                onChange={() => handleToggleVisit(visit.uid)}
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontWeight: isChecked ? 600 : 400 }}>
                                {visit.label}
                              </Typography>
                            }
                            sx={{ m: 0, flex: 1 }}
                          />

                          {isChecked ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Visit Price:
                              </Typography>
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{ min: 0, step: "0.01" }}
                                value={allocation?.amount ?? ""}
                                onChange={(e) => handleVisitAmountChange(visit.uid, e.target.value)}
                                placeholder="0.00"
                                onWheel={(e) => {
                                  (e.target as HTMLInputElement).blur();
                                }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                sx={{ width: 110 }}
                              />
                            </Box>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>

                  <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, pt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Service Total:
                    </Typography>
                    <Chip
                      label={`$${parsedAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${selectedVisitsCount} ${selectedVisitsCount === 1 ? "visit" : "visits"})`}
                      color={hasAmount ? "primary" : "default"}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Stack>

      <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Button size="small" startIcon={<Add />} onClick={onAdd} sx={{ textTransform: "none", fontWeight: 700 }}>
          Add other service
        </Button>
        {showFooterTotal ? (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Extra total: ${totalAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        ) : null}
      </Box>
    </>
  );
}

export function InvoiceExtraLineItemsCard(props: Props) {
  const variant = props.variant ?? "card";

  if (variant === "embedded") {
    return (
      <Box sx={{ borderTop: invoiceEditorInnerBorder }}>
        <ExtraLineItemRows {...props} showFooterTotal={false} />
      </Box>
    );
  }

  return (
    <Card elevation={0} sx={invoiceEditorSectionCardSx}>
      <Box sx={{ p: 2, borderBottom: invoiceEditorInnerBorder }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
          Line Items
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", mt: 0.5 }}>
          Add custom invoice rows with title, description, and amount.
        </Typography>
      </Box>
      <ExtraLineItemRows {...props} showFooterTotal />
    </Card>
  );
}
